import type {CodeNavigationInfo} from '@github-ui/code-nav'
import {DuplicateOnKeydownButton} from '@github-ui/code-view-shared/components/DuplicateOnKeydownButton'
import {LatestCommitSingleLine} from '@github-ui/code-view-shared/components/LatestCommit'
import {useOpenPanel, useSetOpenPanel} from '@github-ui/code-view-shared/contexts/OpenPanelContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useShortcut} from '@github-ui/code-view-shared/hooks/shortcuts'
import {useReposAnalytics} from '@github-ui/code-view-shared/hooks/use-repos-analytics'
import type {Blame} from '@github-ui/code-view-types'
import type {CopilotInfo} from '@github-ui/copilot-common-types'
import {CopilotPopover} from '@github-ui/copilot-popover'
import type {BannerProps} from '@github-ui/copilot-spaces/components/SpacesSelectionMenu'
import {sendEvent} from '@github-ui/hydro-analytics'
// eslint-disable-next-line no-restricted-imports
import {ScreenSize, useScreenSize} from '@github-ui/screen-size'
import {ssrSafeLocation} from '@github-ui/ssr-utils'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {useInFileSearchResults} from '@github-ui/use-in-file-search-results'
import {Banner} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useEffect, useEffectEvent, useMemo, useRef, useState} from 'react'

import {useSearchTerm, useSetSearchTerm} from '../contexts/SearchTermContext'
import {useCurrentBlame} from '../hooks/CurrentBlame'
import type {CurrentBlob} from '../hooks/CurrentBlob'
import {CurrentLineRefMapProvider} from '../hooks/CurrentLineRefMap'
import {useCurrentStyledBlob} from '../hooks/CurrentStyledBlob'
import {useCodeNavigation} from '../hooks/use-code-navigation'
import {focusSymbolSearch, useFocusSymbolPane} from '../hooks/use-focus-symbol-pane'
import {stickyHeaderId} from '../hooks/use-sticky-header-height'
import {DELETE_STICKY_LINES_VALUE, useStickyLines} from '../hooks/use-sticky-lines'
import {useStickyHeaderStyle} from '../hooks/use-sticky-observer'
import {updateExpandPreferences} from '../hooks/use-update-panel-expand-preferences'
import {textAreaId} from '../utilities/lines'
import BlobContent from './blob/BlobContent'
import type {CodeLinesHandle} from './blob/BlobContent/Code/code-lines-handle'
import styles from './BlobViewContent.module.css'
import BlobViewHeader from './headers/BlobViewHeader'
import CodeSizeDetails from './headers/header-components/CodeSizeDetails'
import {PanelContent} from './PanelContent'

export function BlobViewContent({
  blame,
  blob,
  symbolsExpanded,
  setValidCodeNav,
  showTree,
  treeToggleElement,
  validCodeNav,
  copilotInfo,
}: {
  blame?: Blame
  blob: CurrentBlob
  symbolsExpanded: boolean
  setValidCodeNav: (show: boolean) => void
  showTree: boolean
  treeToggleElement: React.ReactElement
  validCodeNav: boolean
  copilotInfo?: CopilotInfo
}) {
  const searchTerm = useSearchTerm()
  const setSearchTerm = useSetSearchTerm()
  const path = usePath()
  const symbolsEnabled = blob.symbolsEnabled
  const [stickyHeaderElement, stickyHeaderRef] = useState<Element | null>(null)
  const stickyStyle = useStickyHeaderStyle()

  const [autoFocusSearch, setAutoFocusSearch] = useState(false)
  const [bannerProps, setBannerProps] = useState<BannerProps | null>(null)
  const {openPanel, setOpenPanel} = useOpenPanel()
  const codeLinesHandleRef = useRef<CodeLinesHandle>(null)
  const shouldOpenPanel = useCodeViewOptions().openSymbolsOption.enabled && symbolsEnabled
  const {stylingDirectives, rawLines, colorizedLines} = useCurrentStyledBlob()

  // if narrow, the code symbols panel is a dialog instead of a panel
  const {screenSize} = useScreenSize()

  useFocusSymbolPane(focusSearch => {
    if (focusSearch) {
      setAutoFocusSearch(true)
    }
  })

  const {sendRepoClickEvent} = useReposAnalytics()

  const isFirstRenderRef = useRef(true)

  const onPathChange = useEffectEvent(() => {
    if (!isFirstRenderRef.current) {
      //remove sticky lines when navigating to a new file
      setStickyLines(DELETE_STICKY_LINES_VALUE, true)

      //reset panel when navigating to new file
      setSearchTerm('')

      // Don't autofocus search when navigating
      setAutoFocusSearch(false)

      setSearchingText({selectedText: '', lineNumber: -1, offset: 0})
    } else {
      isFirstRenderRef.current = false
      // If this is the first render, open the panel if we have text selected from the hash
      if (searchingText.selectedText && !openPanel && shouldOpenPanel && symbolsExpanded) {
        openCodeNav()
      }
    }
  })

  useEffect(() => {
    void path
    onPathChange()
  }, [path])

  const onScrollEvent = useEffectEvent(() => {
    if (window.scrollY < 300) {
      //if the user scrolled to the top, we want to make sure all sticky lines are cleared out in that situation
      setStickyLines(DELETE_STICKY_LINES_VALUE, true)
    }
  })

  useEffect(() => {
    let ticking = false
    const handler = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          onScrollEvent()
          ticking = false
        })
        ticking = true
      }
    }
    // rather than set up an intersection observer on a particular element, we only want to know if the user has scrolled
    // to the very top of the page. The element would never really intersect because it is sticky, so this is the only
    // realistic way for us to determine if the user has scrolled to the top of the page.
    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('scroll', handler)
    return () => {
      window.removeEventListener('scroll', handler)
    }
  }, [])

  const hasBlame = !!useCurrentBlame()

  const {currentStickyLines, setStickyLines} = useStickyLines()
  const openCodeNav = useCallback(() => {
    if (!hasBlame && shouldOpenPanel) {
      setOpenPanel('codeNav')
      sendRepoClickEvent('BLOB_SYMBOLS_MENU.OPEN_WITH_SYMBOL')
      // Defer localStorage write to avoid blocking interaction
      requestIdleCallback(() => localStorage.setItem('codeNavOpen', 'codeNav'))
      updateExpandPreferences(null, true, null)
    }
  }, [hasBlame, shouldOpenPanel, setOpenPanel, sendRepoClickEvent])

  const {isCodeNavLoading, codeNavInfo, showCodeNavWithSymbol, showCodeNavForToken, searchingText, setSearchingText} =
    useCodeNavigation(blob, openCodeNav, setValidCodeNav, ssrSafeLocation.hash, hasBlame)

  const {searchStatus, searchResults, setSearchResults, focusedSearchResult, setFocusedSearchResult} =
    useInFileSearchResults(codeNavInfo, searchTerm)

  const {
    headerInfo: {toc},
  } = blob

  // store if we've rendered with no openPanel
  // think of this like a useRef that resets when codeNavInfo changes (which happens when the user navigates to a new file)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const hasRenderedWithNoOpenPanel = useMemo(() => ({value: false}), [codeNavInfo])
  hasRenderedWithNoOpenPanel.value = !openPanel || hasRenderedWithNoOpenPanel.value

  const showPanel =
    openPanel !== undefined &&
    codeNavInfo &&
    validCodeNav &&
    // we show the symbol panel by default, but if there are no symbols, we shouldn't open the symbol panel. if
    // hasRenderedWithNoOpenPanel.value is true, that implies the user has deliberately requested the symbols panel
    !(codeNavInfo.symbols.length === 0 && openPanel === 'codeNav' && !hasRenderedWithNoOpenPanel.value) &&
    // if we don't have a toc, don't show the toc
    !(!toc && openPanel === 'toc') &&
    !(openPanel === 'edit') &&
    // don't show the symbol panel if it's disabled
    !(!symbolsEnabled && openPanel === 'codeNav')
  const actualOpenPanel = showPanel ? openPanel : undefined

  // if we're not showing the panel, setOpenPanel to maintain consistency
  useEffect(() => {
    if (openPanel && !showPanel) {
      setOpenPanel(undefined)
    }
  }, [openPanel, setOpenPanel, showPanel])

  useEffect(() => {
    try {
      sendEvent('blob-size', {
        // for files over the size limit, lines will be 0
        lines: stylingDirectives?.length,
        // for files over the size limit, truncatedSloc will not be reported
        truncatedSloc: blob.headerInfo?.lineInfo.truncatedSloc,
        // for files over the size limit, truncatedLoc will not be reported
        truncatedLoc: blob.headerInfo?.lineInfo.truncatedLoc,
        // for files over the size limit length will be 0
        length: rawLines?.reduce((total, line) => total + line.length, 0) ?? 0,
        // for files over the size limit, humanLength will be reported
        humanLength: blob.headerInfo?.blobSize,
      })
    } catch {
      // don't want to crash the page just for analytics
    }
  }, [blob, stylingDirectives, rawLines])

  return (
    <CurrentLineRefMapProvider>
      {codeNavInfo && <FindSymbolShortcut codeNavInfo={codeNavInfo} />}
      <LatestCommitSingleLine />
      {bannerProps && (
        <div className="pr-mb-3">
          <Banner {...bannerProps} />
        </div>
      )}
      <div className="d-flex flex-row">
        <div
          className={clsx(
            'container',
            styles.blobContainer,
            actualOpenPanel && codeNavInfo && screenSize > ScreenSize.medium && styles.blobContainerWithPanel,
          )}
        >
          <div className={clsx('react-code-size-details-banner', styles.codeSizeDetails)}>
            <CodeSizeDetails className="react-code-size-details-banner" />
            <CopilotPopover
              copilotInfo={copilotInfo}
              className="react-code-size-details-banner"
              view={blame ? 'blame' : 'preview'}
            />
          </div>
          <div
            className={clsx('react-blob-view-header-sticky', styles.stickyHeader)}
            id={stickyHeaderId}
            ref={stickyHeaderRef}
          >
            <BlobViewHeader
              currentStickyLines={currentStickyLines}
              focusedSearchResult={focusedSearchResult}
              openPanel={actualOpenPanel}
              searchingText={searchingText}
              searchResults={searchResults}
              setFocusedSearchResult={setFocusedSearchResult}
              setOpenPanel={setOpenPanel}
              showTree={showTree}
              stickyHeaderElement={stickyHeaderElement}
              treeToggleElement={treeToggleElement}
              validCodeNav={validCodeNav}
              copilotInfo={copilotInfo}
              colorizedLines={colorizedLines}
              onBannerChange={setBannerProps}
            />
          </div>

          <div className={styles.blobContentWrapper}>
            <BlobContent
              blobLinesHandle={codeLinesHandleRef}
              setOpenPanel={setOpenPanel}
              validCodeNav={validCodeNav}
              codeNavInfo={codeNavInfo}
              onCodeNavTokenSelected={showCodeNavForToken}
              onLineStickOrUnstick={setStickyLines}
              searchResults={searchResults}
              focusedSearchResult={focusedSearchResult}
            />
          </div>
        </div>
        {actualOpenPanel && codeNavInfo ? (
          <>
            {/* This is a bit of a hack for SSR - we want the symbols panel to be shifted down when the
                Code size details are rendered above the blob (instead of in the blob header)
                Because that item needs to be inside the container above for the container query to work
                We need to shift the symbols panel ourselves when the content size is right */}
            <div className={styles.symbolsPanelSpacer} />
            <PanelContent
              stickyStyle={stickyStyle}
              stickyHeaderElement={stickyHeaderElement}
              openPanel={actualOpenPanel}
              isCodeNavLoading={isCodeNavLoading}
              codeNavInfo={codeNavInfo}
              setOpenPanel={setOpenPanel}
              showCodeNavWithSymbol={showCodeNavWithSymbol}
              searchingText={searchingText}
              setSearchingText={setSearchingText}
              searchResults={searchResults}
              searchStatus={searchStatus}
              setSearchResults={setSearchResults}
              setFocusedSearchResult={setFocusedSearchResult}
              autoFocusSearch={autoFocusSearch}
            />
          </>
        ) : null}
      </div>
    </CurrentLineRefMapProvider>
  )
}

function FindSymbolShortcut({codeNavInfo}: {codeNavInfo: CodeNavigationInfo}) {
  const {sendRepoKeyDownEvent} = useReposAnalytics()
  const {findSymbolShortcut} = useShortcut()
  const setOpenPanel = useSetOpenPanel()

  // Don't support `r` hotkey if no symbols exist for the document
  if (codeNavInfo.symbols.length === 0) {
    return null
  }

  return (
    <DuplicateOnKeydownButton
      buttonFocusId={textAreaId}
      buttonHotkey={findSymbolShortcut.hotkey}
      onButtonClick={() => {
        setOpenPanel('codeNav')
        focusSymbolSearch()
        sendRepoKeyDownEvent('FIND_SYMBOL')
      }}
    />
  )
}
